// Feedback buckets: which section (Active / Feature requests / Resolved) a conversation lives in.
// Single source of truth for the dealer dialog tabs, the bubble badge and the admin counts.

import { subDays } from 'date-fns';
import { Lightbulb } from 'lucide-react';
import type { FeedbackConversation, FeedbackStatus } from '../types';

/** Stable `feedback_categories.name` of the Feature Request category */
export const FEATURE_REQUEST_CATEGORY = 'feature_request';

/** Badge shown in place of "In Progress" for confirmed feature requests */
export const featureRequestBadge = {
  label: 'Feature request',
  color: 'bg-violet-100 dark:bg-violet-900 text-violet-800 dark:text-violet-200',
  icon: Lightbulb
};

/**
 * Resolved feedback is archived this many days after it is resolved (daily pg_cron job
 * `prod.auto_archive_resolved_feedback(30)`). Archiving only changes status; rows stay in the DB.
 * The dealer dialog shows resolved items for the same window so the two never disagree.
 */
export const ARCHIVE_AFTER_DAYS = 30;

export type FeedbackBucket = 'active' | 'feature_requests' | 'resolved';

/** Admin status filter: a real status, or the Feature requests section */
export type FeedbackStatusFilter = FeedbackStatus | 'feature_requests';

type BucketFields = Pick<FeedbackConversation, 'status' | 'category_name'>;
type DatedBucketFields = BucketFields & Pick<FeedbackConversation, 'resolved_at' | 'updated_at'>;

/**
 * A feature request an admin has confirmed: Feature Request category AND in progress.
 * An `open` feature request is still the dealer's own label and stays in Active until reviewed.
 */
export const isConfirmedFeatureRequest = (conv: BucketFields): boolean =>
  conv.category_name === FEATURE_REQUEST_CATEGORY && conv.status === 'in_progress';

export const getFeedbackBucket = (conv: BucketFields): FeedbackBucket | undefined => {
  if (isConfirmedFeatureRequest(conv)) return 'feature_requests';
  if (conv.status === 'open' || conv.status === 'in_progress') return 'active';
  if (conv.status === 'resolved') return 'resolved';
  return undefined;
};

/** Whether a conversation adds to the floating bubble's count (same rule as the Active tab) */
export const countsTowardBadge = (conv: BucketFields): boolean =>
  getFeedbackBucket(conv) === 'active';

/** Admin status filter match; "In progress" excludes confirmed feature requests */
export const matchesStatusFilter = (conv: BucketFields, filter: FeedbackStatusFilter): boolean => {
  if (filter === 'feature_requests') return isConfirmedFeatureRequest(conv);
  if (filter === 'in_progress')
    return conv.status === 'in_progress' && !isConfirmedFeatureRequest(conv);
  return conv.status === filter;
};

const resolvedTime = (c: DatedBucketFields) => new Date(c.resolved_at ?? c.updated_at).getTime();

/** Split conversations into the dealer dialog's tabs, keeping input order */
export const splitConversationsByBucket = <T extends DatedBucketFields>(
  conversations: T[],
  now: Date = new Date()
): { active: T[]; featureRequests: T[]; resolved: T[] } => {
  const resolvedCutoff = subDays(now, ARCHIVE_AFTER_DAYS).getTime();
  const active: T[] = [];
  const featureRequests: T[] = [];
  const resolved: T[] = [];

  for (const c of conversations) {
    const bucket = getFeedbackBucket(c);
    if (bucket === 'active') {
      active.push(c);
    } else if (bucket === 'feature_requests') {
      featureRequests.push(c);
    } else if (bucket === 'resolved' && resolvedTime(c) >= resolvedCutoff) {
      resolved.push(c);
    }
  }

  return { active, featureRequests, resolved };
};
